import { Component, Input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { Observable, of } from 'rxjs';
import { map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { InventoryItem } from '../common/inventory-item';
import { ItemListItemRemovedEvent } from '../common/item-list/item-list.component';
import { InventoryListItem } from '../common/item-list/type';
import { SelectedItem } from '../item-selector/interface';
import { ItemSelectorServiceV2, SelectionOptions } from '../item-selector/item-selector-v2.service';
import { PackageContentsService } from '../services/package-contents.service';

export interface ItemSelectionCartConfig {
  partnerId: string;
  marketId: string;
  enforceDependencies: boolean;
}

@Component({
  selector: 'inventory-ui-item-selection-cart',
  templateUrl: './item-selection-cart.component.html',
  styleUrls: ['./item-selection-cart.component.scss'],
})
export class ItemSelectionCartComponent {
  @Input() config!: ItemSelectionCartConfig;

  cartItems = toSignal(this.initCartItems());

  constructor(
    private itemSelectorService: ItemSelectorServiceV2,
    private packageContentsService: PackageContentsService,
    private confirmationModalService: OpenConfirmationModalService,
    private translateService: TranslateService,
  ) {}

  removeAll(): void {
    this.confirmationModalService
      .openModal({
        title: this.translateService.instant('ITEM_SELECTION_CART.CLEAR_ALL_CONFIRMATION_MODAL.TITLE'),
        message: this.translateService.instant('ITEM_SELECTION_CART.CLEAR_ALL_CONFIRMATION_MODAL.MESSAGE'),
        confirmButtonText: this.translateService.instant('COMMON.ACTIONS.CLEAR'),
      })
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.itemSelectorService.clearSelection(true);
      });
  }

  removeItem(event: ItemListItemRemovedEvent): void {
    if (event.parentItemId && event.isNested) {
      const parentItemId = event.parentItemId!;
      const parentItem = this.cartItems()!.find((i) => i.itemId === event.parentItemId)!;
      if (parentItem.nestedItems!.length <= 1) {
        this.itemSelectorService.removeItemId(parentItemId, 'user', this.selectionOptionsFromConfig());
        return;
      }

      this.confirmAndRemoveNestedItem(event.itemId, parentItemId);
      return;
    }

    this.itemSelectorService.removeItemId(event.itemId, 'user', this.selectionOptionsFromConfig());
  }

  private confirmAndRemoveNestedItem(nestedItemId: string, parentItemId: string): void {
    const parentItem = this.cartItems()!.find((i) => i.itemId === parentItemId)!;
    this.confirmationModalService
      .openModal({
        title: this.translateService.instant('ITEM_SELECTION_CART.REMOVE_ITEM_CONFIRMATION_MODAL.TITLE'),
        message: this.translateService.instant('ITEM_SELECTION_CART.REMOVE_ITEM_CONFIRMATION_MODAL.MESSAGE', {
          parentItemName: parentItem.name,
        }),
        confirmButtonText: this.translateService.instant('COMMON.ACTIONS.REMOVE'),
        type: 'warn',
      })
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }

        const remainingNestedItems = parentItem.nestedItems!.filter((ni) => ni.itemId !== nestedItemId);
        this.itemSelectorService.removeItemId(parentItemId, 'user', this.selectionOptionsFromConfig());
        remainingNestedItems.forEach((ni) =>
          this.itemSelectorService.updateSelection(true, ni, this.selectionOptionsFromConfig()),
        );
      });
  }

  private selectionOptionsFromConfig(): SelectionOptions {
    return {
      partnerId: this.config.partnerId,
      marketId: this.config.marketId,
      enforceDependencies: this.config.enforceDependencies,
    };
  }

  private initCartItems(): Observable<InventoryListItem[]> {
    const selectedItems$ = toObservable(this.itemSelectorService.selectedItems);
    const cartItems$: Observable<InventoryListItem[]> = selectedItems$.pipe(
      switchMap((selectedItems) => {
        const selectedPackageItems = selectedItems.filter((item) => item.type === 'package');
        const selectedPackageItemIds = selectedPackageItems.map((item) => item.itemId);
        if (!selectedPackageItemIds.length) {
          return of(new Map());
        }

        return this.packageContentsService.fetchMultiPackageItems(
          selectedPackageItemIds,
          this.config.partnerId,
          this.config.marketId,
        );
      }),
      withLatestFrom(selectedItems$),
      map(([packageItemsMap, selectedItems]) => this.buildCartItems(packageItemsMap, selectedItems)),
    );
    return cartItems$;
  }

  private buildCartItems(
    packageItemsMap: Map<string, InventoryItem[]>,
    selectedItems: SelectedItem[],
  ): InventoryListItem[] {
    const cartItems = selectedItems?.map((selectedItem) => {
      if (selectedItem.type !== 'package') {
        return selectedItem;
      }

      const packageItem: InventoryListItem = {
        ...selectedItem,
      };
      const nestedItems = packageItemsMap.get(selectedItem.itemId);
      if (nestedItems?.length) {
        packageItem.nestedItems = nestedItems;
      }
      return packageItem;
    });
    return cartItems ?? [];
  }
}
